/**
 * 📦 종료 탭으로 넘기기 (v2.215.0 · 2026-09-29)
 *
 * 사용자 지시: 「상태가 종료로 바뀌어도 종료 탭으로 자동으로 넘기지 말고 그냥 진행중 탭에 유지해줘.
 *   목록 상단에 종료 탭으로 넘기기 버튼을 만들어서 누르면 상태가 ongoing 이 아닌 건들의 목록을 나오게 해서
 *   체크해서 종료 탭으로 넘길 수 있도록 해줘.」
 *
 * · 목록 = 진행 중 탭에 있는 **종료 상태**(수주·실주·무효·거절) 딜 — 서버가 골라 준다(`/archive/candidates`).
 *   연장·처리 중·승인대기는 아직 진행 중이라 넣지 않는다(09-29 사용자 「추천대로」).
 * · 처음엔 **아무것도 체크하지 않는다** — 사람이 골라 넘긴다([모두 선택] 있음).
 * · 넘길 때 서버가 다시 거른다 — 그 사이 누가 상태를 진행으로 되돌렸으면 그 딜은 건너뛰고 알려 준다.
 * · ⚠배경을 눌러도 닫히지 않는다(check:modal) — 닫기는 [취소]·× 로만.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { archiveDeals, listArchiveCandidates, type DealRow } from '../lib/deal-registration-api';
import { STATUS_BADGE, dealCompanyLabel, dealEndUser, dealTopic } from '../lib/deal-display';
import { statusShort } from './DealStatusSelect';
import { LoadingLines } from './ui/Skeleton';

export function DealArchiveModal({
  vendor,
  onClose,
  onDone,
}: {
  vendor: string;
  onClose: () => void;
  /** 넘긴 뒤 — 몇 건 넘겼고 몇 건 건너뛰었나 */
  onDone: (moved: number, skipped: number) => void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const cands = useQuery({
    queryKey: ['deals', 'archive-candidates', vendor],
    queryFn: () => listArchiveCandidates(vendor),
    staleTime: 0,
  });
  const rows: DealRow[] = useMemo(() => cands.data ?? [], [cands.data]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [error, setError] = useState('');

  const allOn = rows.length > 0 && rows.every((r) => picked.has(r.id));
  const toggle = (id: number): void => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = (): void => setPicked(allOn ? new Set() : new Set(rows.map((r) => r.id)));

  const move = useMutation({
    mutationFn: () => archiveDeals([...picked]),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['deals'] });
      onDone(r.moved, r.skipped.length);
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-[980px] max-w-[97vw] max-h-[92vh] flex flex-col" data-deal-archive-modal>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t">
          <h3 className="ui-subsection-title">📦 종료 탭으로 넘기기</h3>
          <button type="button" onClick={onClose} aria-label="닫기" disabled={move.isPending}
            className="ui-close">×</button>
        </div>

        <div className="overflow-y-auto px-5 py-3 space-y-2">
          <p className="text-xs text-slate-500 leading-5">
            진행 중 탭에 남아 있는 <b className="text-slate-700">종료 상태(수주·실주·무효·거절)</b> 딜입니다. 체크한 딜을 종료 탭으로 넘깁니다.
            넘긴 딜은 <b className="text-slate-700">24일 검토 표 · 25일 엑셀에서 빠집니다.</b>
            {' '}잘못 넘겼으면 종료 탭에서 상태를 진행(On-going)으로 바꾸면 진행 중 탭으로 돌아옵니다.
          </p>

          {cands.isLoading && <div className="py-8 text-center text-slate-400 text-sm"><LoadingLines /></div>}
          {cands.isError && <div className="py-8 text-center text-rose-600 text-sm">목록을 불러오지 못했습니다 — {(cands.error as Error).message}</div>}
          {cands.data && rows.length === 0 && (
            <div className="py-8 text-center text-slate-500 text-sm">넘길 딜이 없습니다 — 진행 중 탭에 종료 상태 딜이 없습니다.</div>
          )}

          {rows.length > 0 && (
            <table className="data-table dense w-full text-xs" data-archive-table>
              <thead>
                <tr>
                  <th className="w-[34px] text-center">
                    <input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="모두 선택" />
                  </th>
                  <th className="w-[74px] text-center">상태</th>
                  <th className="w-[86px] text-left whitespace-nowrap">종료일</th>
                  <th className="w-[86px] text-left whitespace-nowrap">신청일</th>
                  <th className="text-left min-w-[120px]">제안사</th>
                  <th className="text-left min-w-[120px]">엔드유저</th>
                  <th className="text-left min-w-[140px]">프로젝트</th>
                  <th className="w-[64px] text-left whitespace-nowrap">담당자</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => toggle(r.id)} className={`cursor-pointer ${picked.has(r.id) ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                    <td className="text-center">
                      <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)}
                        onClick={(e) => e.stopPropagation()} aria-label={`${r.registrationNo || r.id} 고르기`} />
                    </td>
                    <td className="text-center">
                      <span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${STATUS_BADGE[r.status] ?? 'bg-slate-100'}`}>{statusShort(r.status)}</span>
                    </td>
                    <td className="tabular-nums whitespace-nowrap">{r.closedDate ?? '-'}</td>
                    <td className="tabular-nums whitespace-nowrap">{r.appliedDate ?? '-'}</td>
                    <td className="max-w-0"><div className="truncate" title={dealCompanyLabel(r)}>{dealCompanyLabel(r) || '-'}</div></td>
                    <td className="max-w-0"><div className="truncate" title={dealEndUser(r)}>{dealEndUser(r) || '-'}</div></td>
                    <td className="max-w-0"><div className="truncate" title={dealTopic(r)}>{dealTopic(r) || '-'}</div></td>
                    <td className="whitespace-nowrap">{r.ownerName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {error && <div className="text-sm text-rose-600">⚠ {error}</div>}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b flex-wrap">
          <span className="text-xs text-slate-500">{rows.length > 0 ? `${rows.length}건 중 ${picked.size}건 골랐습니다` : ''}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={move.isPending}
              className="btn btn-secondary">
              취소
            </button>
            <button type="button" onClick={() => { setError(''); move.mutate(); }} disabled={move.isPending || picked.size === 0}
              className="btn btn-dark">
              {move.isPending ? '넘기는 중…' : `${picked.size}건 종료 탭으로 넘기기`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
